import { useState } from "react";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { Group, Groups } from "../../types/groups";
import { ModalTypes } from "../../types/views";
import { useTranslation } from "@coworker/locales";
import { FullWidthButton } from "../../../SkapaButton";
import { addGroup } from "../../services/groups.service";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import InformationCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/InformationCircleIconPath";
import { Articles } from "../../types/article";
import { PleaseFillIn } from "../../../Reusable/PleaseFillIn";
import { InputPopupContainer } from "../../styles/styles";
import { useQueryClient } from "@tanstack/react-query";
import { addUniqueArticlesToGroup } from "./GroupModal.helper";
import { logEvent } from "../../../../helpers/tracker";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { QueryKeys } from "../../hooks/queryKeys";

interface CreateGroupModalProps {
  modalVisibility: boolean;
  mediaId: string;
  storeId: string;
  groups: Groups;
  selectedArticles: Articles;
  onTriggerNewModal: (modalType: ModalTypes) => void;
}

export function CreateGroupModal({
  modalVisibility,
  mediaId,
  storeId,
  groups,
  selectedArticles,
  onTriggerNewModal,
}: CreateGroupModalProps) {
  const { showToast } = useToastNotification();
  const [newGroupName, setNewGroupName] = useState<string>("");
  const [isDuplicateGroupName, setIsDuplicateGroupName] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return (
    <FixaModal
      visible={modalVisibility}
      handleCloseBtn={() => {
        onTriggerNewModal(ModalTypes.KEEP_SELECTION);
      }}
    >
      <FixaSheets
        labelledById="media-create-group-modal-header"
        size={"small"}
        header={
          <FixaModalHeader
            id="media-create-group-modal-header"
            ariaCloseTxt="Close"
            title={`${t("createGroupString")}`}
          />
        }
        footer={
          <FixaModalFooter>
            <FullWidthButton
              disabled={isDuplicateGroupName || newGroupName === ""}
              text={t("createGroupString")}
              type={"primary"}
              loading={isLoading}
              onClick={async () => {
                setIsLoading(true);
                const newGroup = {
                  name: newGroupName.trim(),
                  description: "",
                  mediaId: mediaId,
                  storeId,
                } as Partial<Group>;

                let toastMessage = t("groupCreatedString");
                const newGroupId = await addGroup(newGroup);
                if (!newGroupId) {
                  toastMessage = t("backendOperationFailedString");
                  logEvent("ce:articles_selected_creategroup_failed");
                } else {
                  groups.push(newGroup as Group);
                  if (selectedArticles.length > 0) {
                    await addUniqueArticlesToGroup(
                      selectedArticles,
                      newGroupId,
                      queryClient
                    );
                  }
                  logEvent("ce:articles_selected_creategroup");
                }

                showToast(toastMessage);
                setNewGroupName("");
                setIsDuplicateGroupName(false);
                setIsLoading(false);
                await queryClient.invalidateQueries({
                  queryKey: [QueryKeys.GroupsByMedia, mediaId],
                });
                onTriggerNewModal(ModalTypes.DEFAULT_VIEW);
              }}
              data
            />
          </FixaModalFooter>
        }
      >
        <InputPopupContainer>
          <FixaInputField
            id={"newGroupName"}
            type={"text"}
            label={t("nameOfGroupString")}
            value={newGroupName}
            onChange={(event) => {
              const newName = event.target.value as string;
              const isDuplicate = groups.some(
                (group) => group.name.trim() === event.target.value.trim()
              );
              setIsDuplicateGroupName(isDuplicate);
              setNewGroupName(newName);
            }}
          />
          {isDuplicateGroupName && (
            <PleaseFillIn
              show={isDuplicateGroupName}
              alternateWarningTranslation={t("duplicateNameString")}
            />
          )}
          {selectedArticles.length > 0 && (
            <FixaInlineMessage
              title={t("whatsNextString")}
              body={t("addToGroupClarificationString")}
              variant={"informative"}
              ssrIcon={InformationCircleIconPath}
              dismissable
            />
          )}
        </InputPopupContainer>
      </FixaSheets>
    </FixaModal>
  );
}
